import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import PortalLayout from '../../components/portal/PortalLayout';
import portalApi from '../../services/portalApi';
import { Calendar, MapPin, Loader, Briefcase } from 'lucide-react';

const PortalJobs = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = usePortalContext();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState('all'); // all, upcoming, completed

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await portalApi.getJobs(token);
      setJobs(data);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Quoted':
        return 'bg-gray-100 text-gray-700';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-700';
      case 'In Progress':
        return 'bg-purple-100 text-purple-700';
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'Cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatAddress = (job) => {
    const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
    return parts.join(', ');
  };

  const filteredJobs = jobs.filter(job => {
  if (filter === 'upcoming') {
    // Include Scheduled and Quoted jobs
    if (job.status === 'Scheduled' || job.status === 'Quoted') {
      // If there's a scheduled date, make sure it's in the future
      // if (job.scheduledDate) {
      //   return new Date(job.scheduledDate) >= new Date();
      // }
      // If no scheduled date yet (Quoted status), still show as upcoming
      return true;
    }
    return false;
  }
  if (filter === 'completed') {
    return job.status === 'Completed';
  }
  return true; // 'all' - show everything
});

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader size={48} className="text-primary-600 animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">My Jobs</h2>
            <p className="text-gray-600 mt-1">
              {filteredJobs.length} {filter === 'all' ? 'total' : filter} jobs
            </p>
          </div>
        </div>

        {/* Filters */}
<div className="flex gap-2">
  <button
    onClick={() => setFilter('all')}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
      filter === 'all'
        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    All Jobs
  </button>
  <button
    onClick={() => setFilter('upcoming')}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
      filter === 'upcoming'
        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    Upcoming
  </button>
  <button
    onClick={() => setFilter('completed')}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
      filter === 'completed'
        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    Completed
  </button>
</div>

        {/* Jobs List */}
        {filteredJobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Briefcase size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No jobs found</p>
            <button
              onClick={() => navigate('/portal/request-quote')}
              className="btn btn-primary mt-4"
            >
              Request New Service
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Status Badge */}
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>

                    {/* Description */}
                    <h3 className="text-lg font-semibold text-gray-900 mt-3">
                      {job.description || 'Service Request'}
                    </h3>

                    {/* Date */}
                    {job.scheduledDate && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                        <Calendar size={16} />
                        <span>{formatDate(job.scheduledDate)}</span>
                      </div>
                    )}

                    {/* Address */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <MapPin size={16} />
                      <span>{formatAddress(job)}</span>
                    </div>

                    {/* Quote Amount */}
                    {job.quoteAmount > 0 && (
                      <div className="mt-3">
                        <span className="text-sm text-gray-600">Quote: </span>
                        <span className="text-lg font-bold text-primary-600">
                          ${job.quoteAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Invoice Badge */}
                  {job.isInvoiced && (
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Invoiced
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalJobs;